import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  Search,
  Briefcase,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Star,
  MapPin,
  User,
  Camera,
  ChevronDown,
  ChevronUp,
  Award,
  Plus
} from 'lucide-react';

const GET_JOBS = gql`
  query GetJobs($status: JobStatus, $limit: Int) {
    jobs(status: $status, limit: $limit) {
      id
      title
      description
      type
      status
      tokenReward
      isPlatformJob
      claimedBy
      claimedAt
      createdAt
      location {
        city
        state
      }
      schedule {
        deadline
      }
      requirements {
        minTrustScore
        requiredSkills {
          skillId
          name
          tier
        }
      }
      completionData {
        notes
        itemsProcessed
        stopsCompleted
        photos
      }
      approvalData {
        rating
        feedback
        tokensAwarded
      }
    }
  }
`;

const GET_JOB_STATS = gql`
  query GetJobStats {
    jobStats {
      open
      claimed
      pendingApproval
      completed
      disputed
      totalTokensAwarded
    }
  }
`;

const APPROVE_JOB = gql`
  mutation ApproveJobCompletion($jobId: ID!, $rating: Int!, $feedback: String, $bonusTokens: Int) {
    approveJobCompletion(jobId: $jobId, rating: $rating, feedback: $feedback, bonusTokens: $bonusTokens) {
      id
      status
      approvalData {
        tokensAwarded
      }
    }
  }
`;

const REJECT_JOB = gql`
  mutation RejectJobCompletion($jobId: ID!, $reason: String!) {
    rejectJobCompletion(jobId: $jobId, reason: $reason) {
      id
      status
    }
  }
`;

const CREATE_JOB = gql`
  mutation CreateJob($input: CreateJobInput!) {
    createJob(input: $input) {
      id
      title
      status
    }
  }
`;

const AdminJobs = () => {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterLocation, setFilterLocation] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedJob, setExpandedJob] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [approvalData, setApprovalData] = useState({ rating: 5, feedback: '', bonusTokens: 0 });
  const [rejectReason, setRejectReason] = useState('');
  const [newJob, setNewJob] = useState({
    title: '',
    description: '',
    type: 'delivery',
    tokenReward: 10,
    isPlatformJob: true
  });

  const { loading, error, data, refetch } = useQuery(GET_JOBS, {
    variables: { 
      status: filterStatus !== 'ALL' ? filterStatus : undefined,
      limit: 100
    },
    pollInterval: 30000
  });

  const { data: statsData, refetch: refetchStats } = useQuery(GET_JOB_STATS);

  const [approveJob] = useMutation(APPROVE_JOB, {
    onCompleted: () => {
      refetch();
      refetchStats();
      setShowApprovalModal(false);
      setSelectedJob(null);
    }
  });

  const [rejectJob] = useMutation(REJECT_JOB, {
    onCompleted: () => {
      refetch();
      refetchStats();
      setShowRejectModal(false);
      setSelectedJob(null);
    }
  });

  const [createJob] = useMutation(CREATE_JOB, {
    onCompleted: () => {
      refetch();
      refetchStats();
      setShowCreateModal(false);
      setNewJob({ title: '', description: '', type: 'delivery', tokenReward: 10, isPlatformJob: true });
    }
  });

  const jobs = data?.jobs || [];
  const stats = statsData?.jobStats || {};

  // Extract unique locations for filter dropdown
  const uniqueLocations = useMemo(() => {
    const locationSet = new Set();
    jobs.forEach(job => {
      if (job.location?.city && job.location?.state) {
        locationSet.add(`${job.location.city}, ${job.location.state}`);
      } else if (job.location?.city) {
        locationSet.add(job.location.city);
      } else if (job.location?.state) {
        locationSet.add(job.location.state);
      }
    });
    return Array.from(locationSet).sort();
  }, [jobs]);

  // Filter jobs by search, status, and location
  const filteredJobs = jobs.filter(job => {
    // Search filter
    const matchesSearch = 
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.worker?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Location filter
    let matchesLocation = true;
    if (filterLocation !== 'ALL') {
      const jobLocation = job.location?.city && job.location?.state
        ? `${job.location.city}, ${job.location.state}`
        : job.location?.city || job.location?.state || '';
      matchesLocation = jobLocation === filterLocation;
    }
    
    return matchesSearch && matchesLocation;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'claimed': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'pending_approval': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'disputed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleApprove = (job) => {
    setSelectedJob(job);
    setApprovalData({ rating: 5, feedback: '', bonusTokens: 0 });
    setShowApprovalModal(true);
  };

  const handleReject = (job) => {
    setSelectedJob(job);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const submitApproval = () => {
    approveJob({
      variables: {
        jobId: selectedJob.id,
        rating: approvalData.rating,
        feedback: approvalData.feedback,
        bonusTokens: approvalData.bonusTokens
      }
    });
  };

  const submitRejection = () => {
    rejectJob({
      variables: {
        jobId: selectedJob.id,
        reason: rejectReason
      }
    });
  };

  const submitCreateJob = () => {
    createJob({
      variables: {
        input: newJob
      }
    });
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-red-600">Error loading jobs: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="mr-3 p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Jobs Management</h1>
              <p className="text-sm text-gray-600">Approve completions and manage platform jobs</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={18} />
              Create Job
            </button>
            <button
              onClick={() => { refetch(); refetchStats(); }}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white px-6 py-4 border-b">
        <div className="grid grid-cols-6 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.open || 0}</div>
            <div className="text-sm text-gray-600">Open</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.claimed || 0}</div>
            <div className="text-sm text-gray-600">Claimed</div>
          </div>
          <div className="text-center cursor-pointer" onClick={() => setFilterStatus('pending_approval')}>
            <div className="text-2xl font-bold text-orange-600">{stats.pendingApproval || 0}</div>
            <div className="text-sm text-gray-600">Pending Approval</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.completed || 0}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.disputed || 0}</div>
            <div className="text-sm text-gray-600">Disputed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.totalTokensAwarded || 0}</div>
            <div className="text-sm text-gray-600">Tokens Awarded</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white px-6 py-4 border-b">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by title or worker..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Status</option>
            <option value="open">Open</option>
            <option value="claimed">Claimed</option>
            <option value="in_progress">In Progress</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="completed">Completed</option>
            <option value="disputed">Disputed</option>
          </select>
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Locations</option>
            {uniqueLocations.map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
        </div>
        
        {/* Active Filters Summary */}
        {(filterStatus !== 'ALL' || filterLocation !== 'ALL' || searchTerm) && (
          <div className="flex items-center gap-2 mt-3 text-sm">
            <span className="text-gray-500">Active filters:</span>
            {filterStatus !== 'ALL' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                Status: {filterStatus}
                <button onClick={() => setFilterStatus('ALL')} className="ml-1 hover:text-blue-900">&times;</button>
              </span>
            )}
            {filterLocation !== 'ALL' && (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                <MapPin size={12} />
                {filterLocation}
                <button onClick={() => setFilterLocation('ALL')} className="ml-1 hover:text-green-900">&times;</button>
              </span>
            )}
            {searchTerm && (
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full flex items-center gap-1">
                Search: "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-gray-900">&times;</button>
              </span>
            )}
            <button 
              onClick={() => {
                setFilterStatus('ALL');
                setFilterLocation('ALL');
                setSearchTerm('');
              }}
              className="text-gray-500 hover:text-gray-700 underline ml-2"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Jobs List */}
      <div className="p-6">
        {filteredJobs.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center">
            <Briefcase size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No jobs found</p>
            {(filterStatus !== 'ALL' || filterLocation !== 'ALL' || searchTerm) && (
              <p className="text-sm text-gray-500 mt-2">
                Try adjusting your filters or{' '}
                <button 
                  onClick={() => {
                    setFilterStatus('ALL');
                    setFilterLocation('ALL');
                    setSearchTerm('');
                  }}
                  className="text-blue-600 hover:underline"
                >
                  clear all filters
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Job Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{job.title}</h3>
                          {job.isPlatformJob && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">Platform</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                          {job.worker && (
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {job.worker.email}
                            </span>
                          )}
                          {job.location?.city && (
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {job.location.city}, {job.location.state}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-bold text-lg">{job.tokenReward}</div>
                        <div className="text-xs text-gray-500">tokens</div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                        {job.status.replace('_', ' ')}
                      </span>
                      {expandedJob === job.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedJob === job.id && (
                  <div className="border-t p-4 bg-gray-50">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Left Column */}
                      <div>
                        <h4 className="font-medium mb-2">Job Details</h4>
                        <div className="space-y-2 text-sm">
                          <p><strong>Type:</strong> {job.type}</p>
                          <p><strong>Description:</strong> {job.description || 'No description'}</p>
                          <p><strong>Min Trust Score:</strong> {job.requirements?.minTrustScore || 'N/A'}</p>
                          <p><strong>Created:</strong> {formatDate(job.createdAt)}</p>
                          {job.claimedAt && <p><strong>Claimed:</strong> {formatDate(job.claimedAt)}</p>}
                          {job.schedule?.deadline && <p><strong>Deadline:</strong> {formatDate(job.schedule.deadline)}</p>}
                        </div>

                        {job.requirements?.requiredSkills?.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-medium mb-2">Required Skills</h4>
                            <div className="flex flex-wrap gap-2">
                              {job.requirements.requiredSkills.map((skill, i) => (
                                <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                  {skill.name} (Tier {skill.tier})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {job.completionData && (
                          <div className="mt-4">
                            <h4 className="font-medium mb-2">Completion Notes</h4>
                            <p className="text-sm text-gray-600 bg-white p-2 rounded border">
                              {job.completionData.notes || 'No notes provided'}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Right Column - Photos */}
                      <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <Camera size={16} />
                          Photo Checkpoints ({job.completionData?.photos?.length || 0})
                        </h4>
                        {job.completionData?.photos?.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {job.completionData.photos.map((photo, i) => (
                              <img 
                                key={i}
                                src={photo} 
                                alt={`Completion photo ${i + 1}`}
                                className="w-24 h-24 object-cover rounded"
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No photos submitted</p>
                        )}

                        {/* Approval Data */}
                        {job.approvalData?.rating && (
                          <div className="mt-4 bg-green-50 p-3 rounded">
                            <h4 className="font-medium mb-2">Approval</h4>
                            <div className="flex items-center gap-2">
                              <span>Rating:</span>
                              {[1,2,3,4,5].map(star => (
                                <Star 
                                  key={star} 
                                  size={16} 
                                  className={star <= job.approvalData.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}
                                />
                              ))}
                            </div>
                            <p className="text-sm mt-1">Tokens awarded: {job.approvalData.tokensAwarded}</p>
                            {job.approvalData.feedback && (
                              <p className="text-sm text-gray-600 mt-1">{job.approvalData.feedback}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {job.status === 'pending_approval' && (
                      <div className="mt-4 pt-4 border-t flex gap-3">
                        <button
                          onClick={() => handleApprove(job)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <CheckCircle size={18} />
                          Approve & Award Tokens
                        </button>
                        <button
                          onClick={() => handleReject(job)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          <XCircle size={18} />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Approve Job Completion</h3>
            <p className="text-sm text-gray-600 mb-4">
              Approving "{selectedJob.title}" will award <strong>{selectedJob.tokenReward} tokens</strong> to the worker.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Rating</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(star => (
                    <button
                      key={star}
                      onClick={() => setApprovalData({...approvalData, rating: star})}
                      className="p-1"
                    >
                      <Star 
                        size={24} 
                        className={star <= approvalData.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Bonus Tokens</label>
                <input
                  type="number"
                  value={approvalData.bonusTokens}
                  onChange={(e) => setApprovalData({...approvalData, bonusTokens: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Total: {selectedJob.tokenReward + approvalData.bonusTokens} tokens
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Feedback (optional)</label>
                <textarea
                  value={approvalData.feedback}
                  onChange={(e) => setApprovalData({...approvalData, feedback: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Great work!"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={submitApproval}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
              >
                Approve
              </button>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Reject Job Completion</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will return the job to "in progress" status so the worker can try again.
            </p>
            
            <div>
              <label className="block text-sm font-medium mb-1">Reason for Rejection</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="Please explain why this completion is being rejected..."
                required
              />
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={submitRejection}
                disabled={!rejectReason.trim()}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-300"
              >
                Reject
              </button>
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Job Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Create Platform Job</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  value={newJob.title}
                  onChange={(e) => setNewJob({...newJob, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Delivery Driver Needed"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={newJob.type}
                  onChange={(e) => setNewJob({...newJob, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="delivery">Delivery</option>
                  <option value="pickup">Pickup</option>
                  <option value="processing">Processing</option>
                  <option value="storage">Storage</option>
                  <option value="admin_review">Admin Review</option>
                  <option value="skill_verification">Skill Verification</option>
                  <option value="quality_control">Quality Control</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={newJob.description}
                  onChange={(e) => setNewJob({...newJob, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Token Reward</label>
                <input
                  type="number"
                  value={newJob.tokenReward}
                  onChange={(e) => setNewJob({...newJob, tokenReward: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="1"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={submitCreateJob}
                disabled={!newJob.title.trim()}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
              >
                Create Job
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobs;